/* eslint-disable sonarjs/no-nested-template-literals */
import { insertParams as insertParamsReachRouter } from '@gatsbyjs/reach-router';
import { isEmpty, isObject, isString } from 'lodash';
import { defaultMemoize as memoizeOne } from 'reselect';

import { encodeQueryParams, parseQueryParams } from 'utils/urlUtils';

// insertParamsReachRouter does not handle trailing slash, this is a fix.
export const insertParams = (path, params) => {
	const pathWithParams = insertParamsReachRouter(path, params);
	return path.endsWith(`/`) && !pathWithParams.endsWith(`/`)
		? `${pathWithParams}/`
		: pathWithParams;
};

export function createHref(to) {
	if (isString(to)) return to;
	if (process.env.NODE_ENV === 'development' && (!isObject(to) || !isString(to.pathname))) {
		throw new TypeError(`Argument 'to' has invalid value: ${JSON.stringify(to)}`);
	}
	const { hash, params, pathname, query } = to;

	return `${params ? insertParams(pathname, params) : pathname}${
		isEmpty(query) ? '' : `?${encodeQueryParams(query)}`
	}${hash ? `#${hash}` : ''}`;
}

export function appendQueryParam(url, key, value) {
	try {
		const [baseWithQuery, hash = ''] = url.split('#');

		const urlObj = new URL(baseWithQuery, process.env.SELF_CARE_URL);

		urlObj.searchParams.set(key, value);

		return urlObj.pathname === '/'
			? `${urlObj.origin}${urlObj.search}${hash ? `#${hash}` : ''}`
			: `${urlObj.origin}${urlObj.pathname}${urlObj.search}${hash ? `#${hash}` : ''}`;
	} catch (error) {
		return url;
	}
}

export function createHrefWithRedirect(to) {
	const redirected = process.env.MOB_APP_BUILD ? 'sc_mob-app' : 'sc_web';

	if (isObject(to) && to.pathname) {
		const updatedQuery = {
			...to.query,
			redirected,
		};
		const updatedTo = { ...to, query: updatedQuery };
		return createHref(updatedTo);
	}

	if (isString(to)) {
		return appendQueryParam(to, 'redirected', redirected);
	}

	return '';
}

/**
 * Transforms standard LocationDescriptor to object with parsed `search` params
 * into `query` property. This function is memoized with one last input, since
 * it might be called multiple times with current location
 * @param {LocationDescriptor} location
 * @return EnhancedLocation
 */
export const denormalizeLocation = memoizeOne(function denormalizeLocation(location) {
	return {
		...location,
		hash: location.hash?.replace(/^#/, ''),
		query: parseQueryParams(location.search),
		state: location.state || undefined, // change null to undefined to allow use default desctructuring
	};
});

// This variable is established at boot-time and later subtracted from the history API length - there might be many
// records in a history from previous domains, but we want our history to provide the `length` of our domain only, to
// be able to decide weather to call `history.goBack` in <BackLink />. This solution is not perfect, e.g. it resets
// the `appStartHistoryLength` upon tab refresh. Storing in `sessionStorage` might be preferred but then the
// <BackLink /> might use `goBack` wrongly in cases, where the user was redirected to and from the third party domain
// (e.g. payment gateway), so the _proper_ solution would be much more complicated and currently not worth the effort.
const appStartHistoryLength = typeof window !== 'undefined' ? window.history.length : 1;
export function enhanceHistory(history, navigate = history.navigate) {
	return {
		createHref,
		// eslint-disable-next-line no-underscore-dangle
		_onTransitionComplete: history._onTransitionComplete,
		listen: history.listen,
		navigate,
		get length() {
			return typeof window !== 'undefined' ? window.history.length - appStartHistoryLength : 0;
		},
		get location() {
			return denormalizeLocation(history.location);
		},
		get transitioning() {
			return history.transitioning;
		},
		go: navigate,
		goBack() {
			navigate(-1);
		},
		goForward() {
			navigate(1);
		},
		push(path, state) {
			navigate(createHref(path), { state: state || path.state });
		},
		replace(path, state) {
			navigate(createHref(path), {
				replace: true,
				state: state || path.state,
			});
		},
	};
}

const basePath = (path) => path.split('/', 2)[1];
export const equalBasePath = (pathname1, pathname2) => basePath(pathname1) === basePath(pathname2);

/**
 * @typedef {object} LocationDescriptor
 * Standard react-router location object
 * @see https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/location.md#location
 * @property {string} hash The URL hash fragment
 * @property {string} search The URL query string
 * @property {object} state location-specific state that was provided to e.g. push(path, state) when this location was pushed onto the stack
 * @property {string} pathname The path of the URL
 */

/**
 * @typedef {LocationDescriptor} EnhancedLocation
 * @property {Record<string, string>} params params provided in path (denoted as `:argument` in url declaration)
 * @property {Record<string, string>} query parses query parameters if provided
 */
